import React, { useState, useEffect, useCallback, useRef } from 'react';

import { Check as CheckIcon } from '@styled-icons/boxicons-regular/Check';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  SimpleGrid,
  HStack,
  Select,
  Button,
  Icon,
  Text,
} from '@chakra-ui/react';

import AppTable from 'components/AppTable';
import { ModalRootProps } from 'components/Modal/Root';
import PaginationWrapper from 'components/Pagination';

import useThrottledState from 'hooks/useThrottledState';

import { Pagination, ServicePagination } from 'types/pagination';
import { UnitCost } from 'types/unit-cost';

const ModalUnitCostLookup: React.FC<ModalRootProps> = ({
  onConfirm,
  handleClose,
  ...restProps
}) => {
  const [data, setData] = useState<UnitCost[]>([]);
  const [loading, setLoading] = useState(false);

  const idInputRef = useRef<HTMLInputElement>(null);

  const [initialLoad, setInitialLoad] = useState(false);

  const [pagination, setPagination] = useThrottledState<Pagination>(
    (() => {
      return {
        per_page: 10,
        page: 1,
      };
    })(),
    1000,
  );

  const [servicePagination, setServicePagination] = useState<ServicePagination>(
    { last_page: 1 },
  );

  const getUnitCosts = useCallback(async () => {
    setLoading(true);
    setData([]);

    let unitCosts: UnitCost[] = [];

    try {
      const response = await api.get('/unit/cost', {
        params: {
          page: pagination.page,
          per_page: pagination.per_page,
          'params[status]': 'ACTIVE',
        },
      });

      const unitCostsData = response.data;
      unitCosts = unitCostsData.data;

      const newPagination = {
        last_page: unitCostsData.meta.last_page,
      };

      setServicePagination(newPagination);

      setData(unitCosts);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          `Ocorreu um erro ao buscar as tabelas do Sienge Plataforma.`,
        status: 'error',
      });

      setData([]);
      setServicePagination({ last_page: 1 });
    } finally {
      setLoading(false);
    }
  }, [pagination]);

  useEffect(() => {
    getUnitCosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  const handleConfirm = (unitCost: UnitCost): void => {
    if (onConfirm) onConfirm(unitCost);
    handleClose();
  };

  useEffect(() => {
    if (pagination.page > servicePagination?.last_page) {
      setPagination((oldPagination) => {
        if (oldPagination.page > 1) {
          return {
            ...oldPagination,
            page: 1,
          };
        }

        return oldPagination;
      });
    }
  }, [pagination.page, servicePagination, setPagination]);

  useEffect(() => {
    if (!initialLoad) {
      setInitialLoad(true);
    }
  }, [initialLoad]);

  useEffect(() => {
    if (initialLoad && idInputRef.current) {
      idInputRef.current.focus();
    }
  }, [initialLoad]);

  return (
    <Modal {...restProps} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Selecionar tabela do Sienge Plataforma</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} mt={6}>
            <HStack mb={3}>
              <Text fontSize="smaller">Por página:</Text>
              <Select
                width="auto"
                defaultValue={pagination.per_page}
                onChange={(e) => {
                  setPagination({
                    ...pagination,
                    per_page: Number(e.target.value),
                  });
                }}
              >
                {[5, 10, 20, 50, 100].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            </HStack>
          </SimpleGrid>

          <AppTable
            cols={[
              { field: 'name', description: 'Tabela', sortable: false },
              {
                field: 'actions',
                description: 'Ações',
                sortable: false,
                isCentered: true,
              },
            ]}
            mapping={(item) => ({
              id: item.external_id,
              name: (
                <Text
                  minWidth={{ base: '100%', md: '350px' }}
                  maxWidth={{ base: '100%', md: '768px' }}
                  whiteSpace="pre-wrap"
                >
                  <Text as="span" fontWeight="bold">
                    {item.external_id}
                  </Text>
                  . {item.description}
                </Text>
              ),
              actions: (
                <Button
                  colorScheme="green"
                  leftIcon={<Icon as={CheckIcon} />}
                  onClick={() => handleConfirm(item)}
                >
                  Selecionar
                </Button>
              ),
            })}
            data={data}
            loading={loading}
            onUpdateSort={() => {
              //
            }}
          />

          <PaginationWrapper
            lastPage={data.length === 1 ? 1 : servicePagination?.last_page}
            onPaginate={(selectedPage) => {
              setPagination({ ...pagination, page: selectedPage });
            }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalUnitCostLookup;
